import useSidebarState from '../../../hooks/useSidebarState';
import { getUiLocale } from '../../../utils/uiTranslation';
import { uiText } from '../../../utils/uiTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { database, storage } from '../../../firebase';
import app from '../../../firebase';
import { ref as dbRef, equalTo, get, onValue, orderByChild, push, query, remove, serverTimestamp, set, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ATS_PLANS, BUNDLE_PLANS, getPlanIdFromProductId } from '../../../utils/featureFlags';
import { removePersonalPhoto } from '../profilePhoto';
import {
  COMMON_DEPARTMENTS,
  DEFAULT_COLLABORATOR_PERMISSIONS,
  OWNER_PERMISSIONS,
  TEAM_ACCESS_PROFILE_STORAGE_EVENT,
  TEAM_PERMISSION_SECTIONS,
  buildCommunityChannelOptions,
  buildInvitePayload,
  getEnabledPermissionCount,
  normalizeDepartmentList,
  normalizeMemberRecord,
  normalizePermissionState,
  readTeamAccessProfileStorageCache,
  summarizeDepartmentAccess,
  writeCommunityFeatureSessionCache,
  writeTeamAccessProfileStorageCache
} from '../../../utils/teamAccess';

const functions = getFunctions(app);
const getProfileSessionCacheKey = (userId = '') => `profile-page-cache:${String(userId || '').trim()}`;

const splitDisplayName = (value = '') => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

const getTeamProfileKey = (email = '') =>
  String(email || '')
    .trim()
    .toLowerCase()
    .replace(/[.#$/[\]]/g, '_');

const getTeamProfileStorageKey = (email = '', fallbackId = '') => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (normalizedEmail) {
    return getTeamProfileKey(normalizedEmail);
  }

  return String(fallbackId || '').trim();
};

const getTeamProfileLookupKeys = ({ uid = '', email = '', fallbackId = '' } = {}) => {
  const keys = [];
  const primaryUid = String(uid || fallbackId || '').trim();
  const normalizedEmail = String(email || '').trim().toLowerCase();

  if (primaryUid) {
    keys.push(primaryUid);
  }

  if (normalizedEmail) {
    const legacyEmailKey = getTeamProfileKey(normalizedEmail);
    if (!keys.includes(legacyEmailKey)) {
      keys.push(legacyEmailKey);
    }
  }

  return keys;
};

const getTeamProfileOverlay = (profiles = {}, identity = {}) => {
  const lookupKeys = getTeamProfileLookupKeys(identity);

  for (const profileKey of lookupKeys) {
    if (profileKey && profiles[profileKey]) {
      return profiles[profileKey];
    }
  }

  return {};
};

const getSeatLimitFromPlanId = (planId, productType) => {
  if (!planId || !productType) return null;

  if (productType === 'ats') {
    const plan = ATS_PLANS[planId];
    if (!plan) return null;
    const users = plan.features?.users;
    if (users === 'unlimited') return 'unlimited';
    return typeof users === 'number' ? users : null;
  }

  if (productType === 'bundle') {
    const plan = BUNDLE_PLANS[planId];
    if (!plan) return null;
    const users = plan.features?.users;
    if (users === 'unlimited') return 'unlimited';
    return typeof users === 'number' ? users : null;
  }

  return null;
};

export function useProfilePageData() {
  const { currentUser, logout } = useAuth();
  const { accessType, subscription, trial, planId } = useAccessControl();
  const { t } = useTranslation('profile');
  const { isRTL, language } = useLanguage();

  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [userUID, setUserUID] = useState('');
  const [isCompanyOwner, setIsCompanyOwner] = useState(false);
  const [companyData, setCompanyData] = useState({
    companyName: '',
    city: '',
    country: '',
    description: '',
    logoUrl: '',
    communityChannels: {},
    agencyJobPostingEnabled: false,
    defaultApplicationMethod: 'ats',
    customFeatures: {
      community: true
    }
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState(COMMON_DEPARTMENTS);
  const [isEditing, setIsEditing] = useState(false);
  const [originalCompanyData, setOriginalCompanyData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({
    plan: 'No Plan Chosen Yet.',
    billingDate: 'Next Billing Date: Inapplicable',
    status: 'Status: Inapplicable',
    canManage: false,
    planId: null,
    productType: null,
    seatLimit: null
  });
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [personalPhotoUrl, setPersonalPhotoUrl] = useState('');
  const [approvalJobPostings, setApprovalJobPostings] = useState(false);
  const [approvalOfferLetters, setApprovalOfferLetters] = useState(false);
  const [approvalSettingsLoading, setApprovalSettingsLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [memberEditorLoading, setMemberEditorLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [personalPhotoUploading, setPersonalPhotoUploading] = useState(false);
  const [personalPhotoRemoving, setPersonalPhotoRemoving] = useState(false);
  const [personalPhotoError, setPersonalPhotoError] = useState('');
  const [teamActionKey, setTeamActionKey] = useState('');
  const [approvalBusy, setApprovalBusy] = useState({
    jobPostings: false,
    offerLetters: false
  });
  const updatingApprovalRef = useRef(false);
  const lastToggleTimeRef = useRef(0);
  const [accountForm, setAccountForm] = useState({
    firstName: '',
    lastName: ''
  });
  const [accountEditing, setAccountEditing] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [currentAccessProfile, setCurrentAccessProfile] = useState(() =>
    normalizeMemberRecord({
      email: currentUser?.email || '',
      name: currentUser?.displayName || '',
      permissions: DEFAULT_COLLABORATOR_PERMISSIONS
    })
  );

  const fileInputRef = useRef(null);
  const personalFileInputRef = useRef(null);

  useEffect(() => {
    if (!currentUser) {
      setProfileLoading(true);
      return;
    }

    const cacheKey = getProfileSessionCacheKey(currentUser.uid);
    let restoredFromCache = false;

    try {
      const cachedPayload = sessionStorage.getItem(cacheKey);
      if (cachedPayload) {
        const parsed = JSON.parse(cachedPayload);
        setUserUID(parsed.userUID || '');
        setIsCompanyOwner(parsed.isCompanyOwner === true);
        setCompanyData(parsed.companyData || {
          companyName: '',
          city: '',
          country: '',
          description: '',
          logoUrl: '',
          communityChannels: {},
          agencyJobPostingEnabled: false,
          defaultApplicationMethod: 'ats',
          customFeatures: {
            community: true
          }
        });
        setTeamMembers(Array.isArray(parsed.teamMembers) ? parsed.teamMembers : []);
        setPendingInvites(Array.isArray(parsed.pendingInvites) ? parsed.pendingInvites : []);
        setDepartmentOptions(Array.isArray(parsed.departmentOptions) && parsed.departmentOptions.length > 0 ? parsed.departmentOptions : COMMON_DEPARTMENTS);
        setSubscriptionData(parsed.subscriptionData || {
          plan: 'No Plan Chosen Yet.',
          billingDate: 'Next Billing Date: Inapplicable',
          status: 'Status: Inapplicable',
          canManage: false,
          planId: null,
          productType: null,
          seatLimit: null
        });
        // Auth is authoritative: a cached URL can point to an already removed photo.
        setPersonalPhotoUrl(currentUser.photoURL || '');
        setApprovalJobPostings(parsed.approvalJobPostings === true);
        setApprovalOfferLetters(parsed.approvalOfferLetters === true);
        setCurrentAccessProfile(
          normalizeMemberRecord({
            email: currentUser.email || '',
            name: currentUser.displayName || '',
            permissions: DEFAULT_COLLABORATOR_PERMISSIONS,
            ...(parsed.currentAccessProfile || {})
          })
        );
        setProfileLoading(false);
        restoredFromCache = true;
        authenticateUser().catch(console.error);
      }
    } catch (error) {
      console.error('Error restoring profile session cache:', error);
      sessionStorage.removeItem(cacheKey);
    }

    if (!restoredFromCache) {
      setProfileLoading(true);
      authenticateUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || profileLoading) return;

    try {
      sessionStorage.setItem(
        getProfileSessionCacheKey(currentUser.uid),
        JSON.stringify({
          userUID,
          isCompanyOwner,
          companyData,
          teamMembers,
          pendingInvites,
          departmentOptions,
          subscriptionData,
          personalPhotoUrl,
          approvalJobPostings,
          approvalOfferLetters,
          currentAccessProfile
        })
      );
    } catch (error) {
      console.error('Error writing profile session cache:', error);
    }
  }, [
    approvalJobPostings,
    approvalOfferLetters,
    companyData,
    currentAccessProfile,
    currentUser,
    departmentOptions,
    isCompanyOwner,
    pendingInvites,
    personalPhotoUrl,
    profileLoading,
    subscriptionData,
    teamMembers,
    userUID
  ]);

  useEffect(() => {
    if (!currentUser?.uid) {
      return undefined;
    }

    const applyCachedAccessProfile = (cached) => {
      if (!cached?.accessProfile) {
        return;
      }

      setCurrentAccessProfile(
        normalizeMemberRecord({
          email: currentUser.email || '',
          name: currentUser.displayName || '',
          ...(cached.accessProfile || {})
        })
      );
      setIsCompanyOwner(cached.isCompanyOwner === true);
      if (cached.companyUID) {
        setUserUID(cached.companyUID);
      }
    };

    applyCachedAccessProfile(readTeamAccessProfileStorageCache(currentUser.uid, userUID || ''));

    const handleTeamAccessProfileStorageChange = (event) => {
      const eventUserId = String(event?.detail?.userId || '').trim();
      const eventCompanyUID = String(event?.detail?.companyUID || '').trim();
      const knownCompanyUID = String(userUID || '').trim();

      if (eventUserId !== String(currentUser.uid || '').trim()) {
        return;
      }

      if (knownCompanyUID && eventCompanyUID && eventCompanyUID !== knownCompanyUID) {
        return;
      }

      applyCachedAccessProfile({
        companyUID: eventCompanyUID || knownCompanyUID,
        isCompanyOwner: event?.detail?.isCompanyOwner === true,
        accessProfile: event?.detail?.accessProfile || null
      });
    };

    window.addEventListener(TEAM_ACCESS_PROFILE_STORAGE_EVENT, handleTeamAccessProfileStorageChange);
    return () => window.removeEventListener(TEAM_ACCESS_PROFILE_STORAGE_EVENT, handleTeamAccessProfileStorageChange);
  }, [currentUser, userUID]);

  useEffect(() => {
    setPersonalPhotoUrl(currentUser?.photoURL || '');
  }, [currentUser?.photoURL]);

  useEffect(() => {
    const displayNameParts = splitDisplayName(currentUser?.displayName || '');
    setAccountForm({
      firstName: currentAccessProfile.firstName || displayNameParts.firstName || '',
      lastName: currentAccessProfile.lastName || displayNameParts.lastName || ''
    });
  }, [currentAccessProfile.firstName, currentAccessProfile.lastName, currentUser?.displayName]);

  useEffect(() => {
    const maybeSyncOwnerPhoto = async () => {
      try {
        if (!currentUser || !userUID || !isCompanyOwner || !currentUser.photoURL) return;
        const companyRef = dbRef(database, `companies/${userUID}`);
        const snap = await get(companyRef);
        if (!snap.exists()) return;

        const data = snap.val();
        if (data.ownerPhotoUrl !== currentUser.photoURL) {
          await update(companyRef, { ownerPhotoUrl: currentUser.photoURL });
        }
      } catch (error) {
        console.error('Error syncing owner photo URL:', error);
      }
    };

    maybeSyncOwnerPhoto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.photoURL, userUID, isCompanyOwner]);

  const authenticateUser = async () => {
    try {
      let uid = currentUser.uid;
      const ownerSnap = await get(dbRef(database, `companies/${uid}`));

      if (ownerSnap.exists()) {
        setUserUID(uid);
        setIsCompanyOwner(true);
        const ownerAccessProfile = normalizeMemberRecord({
          uid,
          email: currentUser.email || '',
          name: currentUser.displayName || '',
          permissions: OWNER_PERMISSIONS,
          departmentScope: 'all',
          departments: []
        });
        setCurrentAccessProfile(ownerAccessProfile);
        writeTeamAccessProfileStorageCache(currentUser.uid, uid, {
          companyUID: uid,
          isCompanyOwner: true,
          accessProfile: ownerAccessProfile
        });
        // Persist ownerEmail on the company doc so collaborators can resolve the owner.
        if (currentUser.email) {
          const existing = ownerSnap.val() || {};
          if (!existing.ownerEmail) {
            update(dbRef(database, `companies/${uid}`), { ownerEmail: currentUser.email }).catch(console.error);
          }
        }
        await loadCompanyData(uid);
        await loadTeamData(uid);
        setProfileLoading(false);
        return;
      }

      const collabSnap = await get(dbRef(database, `collaborators/${uid}`));
      if (collabSnap.exists()) {
        const collaboratorData = collabSnap.val() || {};
        uid = collaboratorData.companyId || collaboratorData.company_uid;
        setUserUID(uid);
        setIsCompanyOwner(false);
        let teamProfileData = {};

        if (uid) {
          const profileKeys = getTeamProfileLookupKeys({
            uid: currentUser.uid,
            email: collaboratorData.email || currentUser.email
          });

          for (const profileKey of profileKeys) {
            const teamProfileSnap = await get(dbRef(database, `companies/${uid}/teamAccessProfiles/${profileKey}`));
            if (teamProfileSnap.exists()) {
              teamProfileData = teamProfileSnap.val() || {};
              break;
            }
          }
        }

        const collaboratorAccessProfile = normalizeMemberRecord({
          uid: currentUser.uid,
          email: collaboratorData.email || currentUser.email || '',
          name: currentUser.displayName || '',
          ...collaboratorData,
          ...teamProfileData
        });
        setCurrentAccessProfile(collaboratorAccessProfile);
        writeTeamAccessProfileStorageCache(currentUser.uid, uid, {
          companyUID: uid,
          isCompanyOwner: false,
          accessProfile: collaboratorAccessProfile
        });
        await loadCompanyData(uid);
        await loadTeamData(uid);
        setProfileLoading(false);
        return;
      }

      await logout();
      window.location.href = 'https://www.jobseeqr.co';
    } catch (error) {
      console.error('Authentication error:', error);
      await logout();
      window.location.href = 'https://www.jobseeqr.co';
    } finally {
      setProfileLoading(false);
    }
  };

  const loadCompanyData = async (uid) => {
    try {
      const snapshot = await get(dbRef(database, `companies/${uid}`));
      if (!snapshot.exists()) return;

      const data = snapshot.val();
      setCompanyData({
        companyName: data.companyName || '',
        city: data.city || '',
        country: data.country || '',
        description: data.description || '',
        logoUrl: data.logoUrl || '',
        communityChannels: data.community?.channels || {},
        agencyJobPostingEnabled: data.settings?.agencyJobPostingEnabled === true,
        defaultApplicationMethod: data.settings?.defaultApplicationMethod === 'external' ? 'external' : 'ats',
        customFeatures: {
          community: data.settings?.customFeatures?.community !== false
        }
      });
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const loadTeamData = async (uid) => {
    try {
      const collaboratorsRef = query(dbRef(database, 'collaborators'), orderByChild('companyId'), equalTo(uid));
      const legacyCollaboratorsRef = query(dbRef(database, 'collaborators'), orderByChild('company_uid'), equalTo(uid));
      const invitesRef = dbRef(database, `companies/${uid}/pendingInvites`);
      const teamProfilesRef = dbRef(database, `companies/${uid}/teamAccessProfiles`);
      const postsRef = dbRef(database, `companies/${uid}/posts`);
      const employeesRef = dbRef(database, `companies/${uid}/employees`);

      const [collabSnapshot, legacyCollabSnapshot, invitesSnapshot, teamProfilesSnapshot, postsSnapshot, employeesSnapshot] = await Promise.all([
        get(collaboratorsRef),
        get(legacyCollaboratorsRef),
        get(invitesRef),
        get(teamProfilesRef),
        get(postsRef),
        get(employeesRef)
      ]);

      const members = [];
      const invites = [];
      const discoveredDepartments = new Set(COMMON_DEPARTMENTS);
      const teamProfiles = teamProfilesSnapshot.exists() ? teamProfilesSnapshot.val() || {} : {};
      const memberIdentityIndex = new Set();
      const inviteIdentityIndex = new Set();
      const seenCollaboratorIds = new Set();

      const registerDepartments = (...sources) => {
        sources.forEach((source) => {
          normalizeDepartmentList(source).forEach((department) => discoveredDepartments.add(department));
        });
      };
      const registerMemberIdentity = (entry) => {
        const identityKey = getTeamProfileStorageKey(entry?.email, entry?.uid || entry?.key);
        if (identityKey) memberIdentityIndex.add(identityKey);
      };
      const registerInviteIdentity = (entry) => {
        const identityKey = getTeamProfileStorageKey(entry?.email, entry?.uid || entry?.key);
        if (identityKey) inviteIdentityIndex.add(identityKey);
      };

      const pushCollaboratorsFromSnapshot = (snapshot) => {
        if (!snapshot.exists()) return;

        snapshot.forEach((child) => {
          if (seenCollaboratorIds.has(child.key)) {
            return;
          }

          seenCollaboratorIds.add(child.key);
          const data = child.val() || {};
          const member = normalizeMemberRecord({
            uid: child.key,
            sourceType: 'collaborator',
            ...data,
            ...getTeamProfileOverlay(teamProfiles, {
              uid: child.key,
              email: data.email
            })
          });
          members.push(member);
          registerMemberIdentity(member);
          registerDepartments(member.departments);
        });
      };

      pushCollaboratorsFromSnapshot(collabSnapshot);
      pushCollaboratorsFromSnapshot(legacyCollabSnapshot);

      if (invitesSnapshot.exists()) {
        invitesSnapshot.forEach((child) => {
          const data = child.val() || {};
          const invite = normalizeMemberRecord({
            key: child.key,
            sourceType: 'invite',
            ...data,
            ...getTeamProfileOverlay(teamProfiles, {
              uid: child.key,
              email: data.email,
              fallbackId: child.key
            })
          });
          invites.push(invite);
          registerInviteIdentity(invite);
          registerDepartments(invite.departments);
        });
      }

      Object.entries(teamProfiles).forEach(([profileKey, profileValue]) => {
        const profile = normalizeMemberRecord({
          uid: profileValue?.uid || profileKey,
          key: profileKey,
          sourceType: 'profile',
          ...profileValue
        });
        const identityKey = getTeamProfileStorageKey(profile.email, profile.uid || profile.key || profileKey);

        if (!identityKey || memberIdentityIndex.has(identityKey) || inviteIdentityIndex.has(identityKey)) {
          return;
        }

        members.push(profile);
        registerMemberIdentity(profile);
        registerDepartments(profile.departments);
      });

      if (postsSnapshot.exists()) {
        postsSnapshot.forEach((child) => {
          const post = child.val() || {};
          registerDepartments(post.department);
        });
      }

      if (employeesSnapshot.exists()) {
        employeesSnapshot.forEach((child) => {
          const employee = child.val() || {};
          registerDepartments(
            employee.department,
            employee.profile?.department,
            employee.roleDepartment?.split('/')[1]
          );
        });
      }

      // Prepend the company owner so they always appear first in Team Access.
      // When the current user IS the owner their email comes from Firebase Auth.
      // For collaborators viewing this page the email must be on the company doc.
      const companyDocSnap = await get(dbRef(database, `companies/${uid}`));
      if (companyDocSnap.exists()) {
        const cd = companyDocSnap.val() || {};
        let ownerEmail = String(cd.ownerEmail || cd.contactEmail || cd.email || '').trim().toLowerCase();
        if (!ownerEmail && currentUser?.uid === uid && currentUser?.email) {
          ownerEmail = currentUser.email.toLowerCase();
        }
        if (ownerEmail) {
          const ownerIdentityKey = getTeamProfileStorageKey(ownerEmail, uid);
          if (!memberIdentityIndex.has(ownerIdentityKey)) {
            const ownerEntry = normalizeMemberRecord({
              uid,
              email: ownerEmail,
              name:
                cd.ownerName ||
                (currentUser?.uid === uid ? currentUser?.displayName : null) ||
                cd.contactName ||
                cd.companyName ||
                ownerEmail.split('@')[0],
              firstName: cd.ownerFirstName || '',
              lastName: cd.ownerLastName || '',
              permissions: OWNER_PERMISSIONS,
              departmentScope: 'all',
              departments: [],
              sourceType: 'owner',
            });
            members.unshift(ownerEntry);
          }
        }
      }

      setTeamMembers(members);
      setPendingInvites(invites);
      setDepartmentOptions(Array.from(discoveredDepartments).sort((a, b) => a.localeCompare(b)));
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  };

  useEffect(() => {
    if (subscription && ['active', 'trialing', 'unlimited'].includes(subscription.status)) {
      const productType = subscription.productType || 'ats';
      let effectivePlanId = planId;

      if (!effectivePlanId) {
        const productId = subscription.productId || subscription.product_id;
        if (productId) {
          effectivePlanId = getPlanIdFromProductId(productId);
        } else if (subscription.planId) {
          effectivePlanId = subscription.planId;
        }
      }

      let planName = subscription.subscription_name || null;
      if (!planName && effectivePlanId) {
        if (productType === 'ats' && ATS_PLANS[effectivePlanId]) {
          planName = ATS_PLANS[effectivePlanId].metadata?.subscriptionName || ATS_PLANS[effectivePlanId].displayName;
        } else if (productType === 'bundle' && BUNDLE_PLANS[effectivePlanId]) {
          planName = BUNDLE_PLANS[effectivePlanId].metadata?.subscriptionName || BUNDLE_PLANS[effectivePlanId].displayName;
        }
      }

      if (!planName) {
        planName = productType === 'bundle'
          ? 'Bundle Subscription'
          : productType === 'tse'
            ? 'Talent Search Engine Subscription'
            : 'ATS Subscription';
      }

      let billingDateText = 'Next Billing Date: Inapplicable';
      if (subscription.subscription_end_date) {
        const date = new Date(subscription.subscription_end_date);
        billingDateText = `Next Billing Date: ${date.toLocaleDateString(getUiLocale(), {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}`;
      }

      const seatLimit = getSeatLimitFromPlanId(effectivePlanId, productType);
      let planDisplay = planName;
      if (seatLimit && seatLimit !== 'unlimited') {
        planDisplay = `${planName} (Up to ${seatLimit} seats)`;
      } else if (seatLimit === 'unlimited') {
        planDisplay = `${planName} (Unlimited seats)`;
      }

      setSubscriptionData({
        plan: planDisplay,
        planName,
        billingAt: subscription.subscription_end_date || null,
        statusCode: subscription.status,
        billingDate: billingDateText,
        status: `Status: ${subscription.status}`,
        canManage: true,
        planId: effectivePlanId,
        productType,
        seatLimit
      });
      return;
    }

    setSubscriptionData({
      plan: 'No Plan Chosen Yet.',
      billingDate: 'Next Billing Date: Inapplicable',
      status: 'Status: Inapplicable',
      canManage: false,
      planId: null,
      productType: null,
      seatLimit: null
    });
  }, [subscription, planId]);

  useEffect(() => {
    if (!userUID) {
      setApprovalSettingsLoading(false);
      return undefined;
    }

    setApprovalSettingsLoading(true);
    const settingsRef = dbRef(database, `companies/${userUID}/settings/approvals`);
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const timeSinceLastToggle = Date.now() - lastToggleTimeRef.current;
      if (updatingApprovalRef.current || timeSinceLastToggle < 1200) {
        return;
      }
      if (snapshot.exists()) {
        const data = snapshot.val();
        setApprovalJobPostings(data.jobPostings === true);
        setApprovalOfferLetters(data.offerLetters === true);
      } else {
        setApprovalJobPostings(false);
        setApprovalOfferLetters(false);
      }
      setApprovalSettingsLoading(false);
    });

    return () => unsubscribe();
  }, [userUID]);

  const permissionLookup = useMemo(
    () =>
      TEAM_PERMISSION_SECTIONS.flatMap((section) => section.permissions).reduce((accumulator, permission) => {
        accumulator[permission.key] = t(
          permission.titleKey || `team.permissions.${permission.key}.title`,
          permission.title
        );
        return accumulator;
      }, {}),
    [t]
  );

  // Owner is now included in teamMembers (sourceType === 'owner'), so no +1 needed.
  const seatUsageCount = teamMembers.length + pendingInvites.length;

  const profileStats = useMemo(() => {
    const coveredDepartments = new Set();

    teamMembers.forEach((member) => {
      normalizeDepartmentList(member.departments).forEach((department) => coveredDepartments.add(department));
    });

    pendingInvites.forEach((invite) => {
      normalizeDepartmentList(invite.departments).forEach((department) => coveredDepartments.add(department));
    });

    const scopedMembers = teamMembers.filter((member) => member.departmentScope === 'specific').length;
    const approvers = teamMembers.filter(
      (member) => member.permissions?.canApprove || member.permissions?.canReviewJobs || member.permissions?.canReviewOffers
    ).length;

    return {
      departmentsCovered: coveredDepartments.size,
      scopedMembers,
      approvers
    };
  }, [pendingInvites, teamMembers]);

  const communityChannelOptions = useMemo(
    () => buildCommunityChannelOptions(companyData.communityChannels || {}),
    [companyData.communityChannels]
  );

  const filteredTeamMembers = useMemo(() => {
    const queryValue = searchTerm.trim().toLowerCase();

    return teamMembers.filter((member) => {
      const matchesSearch = !queryValue || [
        member.name,
        member.email,
        member.position,
        summarizeDepartmentAccess(member, language)
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryValue));

      if (!matchesSearch) return false;
      if (teamFilter === 'restricted') return member.departmentScope === 'specific';
      if (teamFilter === 'approvers') {
        return Boolean(member.permissions?.canApprove || member.permissions?.canReviewJobs || member.permissions?.canReviewOffers);
      }

      return teamFilter === 'all' || teamFilter === 'active';
    });
  }, [language, searchTerm, teamFilter, teamMembers]);

  const filteredPendingInvites = useMemo(() => {
    const queryValue = searchTerm.trim().toLowerCase();

    return pendingInvites.filter((invite) => {
      const matchesSearch = !queryValue || [
        invite.name,
        invite.email,
        invite.position,
        summarizeDepartmentAccess(invite, language)
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(queryValue));

      if (!matchesSearch) return false;
      if (teamFilter === 'active') return false;
      if (teamFilter === 'restricted') return invite.departmentScope === 'specific';
      if (teamFilter === 'approvers') {
        return Boolean(invite.permissions?.canApprove || invite.permissions?.canReviewJobs || invite.permissions?.canReviewOffers);
      }

      return teamFilter === 'all' || teamFilter === 'invited';
    });
  }, [language, pendingInvites, searchTerm, teamFilter]);

  const getNormalizedEmail = (value = '') => String(value || '').trim().toLowerCase();

  const isCurrentUserMember = (member = {}) => {
    const memberUid = String(member.uid || member.key || '').trim();
    const memberEmail = getNormalizedEmail(member.email);
    const currentEmail = getNormalizedEmail(currentUser?.email || currentAccessProfile.email);

    return Boolean(
      (memberUid && currentUser?.uid && memberUid === currentUser.uid) ||
      (memberEmail && currentEmail && memberEmail === currentEmail)
    );
  };

  const hasWorkspaceAdminPermissions = (permissions = {}) => {
    const normalized = normalizePermissionState(permissions);
    return normalized.canManageTeam || normalized.canManageCompanySettings;
  };

  const isWorkspaceAdminMember = (member = {}) => hasWorkspaceAdminPermissions(member.permissions);

  const findTeamMemberByIdentity = ({ uid = '', email = '' } = {}) => {
    const normalizedUid = String(uid || '').trim();
    const normalizedEmail = getNormalizedEmail(email);

    return teamMembers.find((member) => {
      const memberUid = String(member.uid || member.key || '').trim();
      const memberEmail = getNormalizedEmail(member.email);
      return Boolean(
        (normalizedUid && memberUid && normalizedUid === memberUid) ||
        (normalizedEmail && memberEmail && normalizedEmail === memberEmail)
      );
    });
  };

  const getTeamActionRestriction = (member = {}) => {
    if (!canManageTeam || member.sourceType === 'owner') {
      return 'locked';
    }

    if (!isCompanyOwner && isCurrentUserMember(member)) {
      return 'self';
    }

    if (!isCompanyOwner && isWorkspaceAdminMember(member)) {
      return 'admin';
    }

    return '';
  };

  const canEditTeamMember = (member = {}) => Boolean(canManageTeam && !getTeamActionRestriction(member));
  const canRemoveTeamMember = (member = {}) => Boolean(canManageTeam && !getTeamActionRestriction(member));

  const getTeamMemberActionStatus = (member = {}) => {
    const restriction = getTeamActionRestriction(member);
    if (restriction === 'self') {
      return t('team.currentUserManagedByOwner', 'Your access is owner-managed');
    }
    if (restriction === 'admin') {
      return t('team.adminManagedByOwner', 'Owner-managed admin');
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    if (!canManageCompanySettings) {
      return;
    }

    setCompanyData((previous) => ({ ...previous, [field]: value }));
  };

  const handleAccountInputChange = (field, value) => {
    setAccountForm((previous) => ({
      ...previous,
      [field]: value
    }));
  };

  const handleAccountEditToggle = () => {
    if (!accountEditing) {
      const displayNameParts = splitDisplayName(currentUser?.displayName || '');
      setAccountForm({
        firstName: currentAccessProfile.firstName || displayNameParts.firstName || '',
        lastName: currentAccessProfile.lastName || displayNameParts.lastName || ''
      });
      setAccountEditing(true);
      return;
    }

    setAccountEditing(false);
  };

  const handleAccountSave = async () => {
    if (!currentUser) return;

    const firstName = String(accountForm.firstName || '').trim();
    const lastName = String(accountForm.lastName || '').trim();
    const displayName = [firstName, lastName].filter(Boolean).join(' ').trim();

    if (!displayName) {
      alert(uiText('Please enter at least a first or last name.'));
      return;
    }

    setAccountSaving(true);

    try {
      await updateProfile(currentUser, { displayName });

      const nextProfileData = {
        firstName,
        lastName,
        name: displayName
      };

      if (isCompanyOwner && userUID) {
        await update(dbRef(database, `companies/${userUID}`), {
          ownerFirstName: firstName,
          ownerLastName: lastName,
          ownerName: displayName
        });
      } else if (currentUser.uid) {
        await update(dbRef(database, `collaborators/${currentUser.uid}`), nextProfileData);

        const normalizedEmail = String(currentAccessProfile.email || currentUser.email || '').trim();
        if (userUID) {
          await update(
            dbRef(database, `companies/${userUID}/teamAccessProfiles/${currentUser.uid}`),
            {
              ...nextProfileData,
              uid: currentUser.uid,
              email: normalizedEmail,
              updatedAt: serverTimestamp()
            }
          );
        }
      }

      setCurrentAccessProfile((previous) => normalizeMemberRecord({
        ...previous,
        ...nextProfileData
      }));
      setAccountEditing(false);
    } catch (error) {
      console.error('Error saving account profile:', error);
      alert(uiText('Failed to update your profile name. Please try again.'));
    } finally {
      setAccountSaving(false);
    }
  };

  const handleSave = async () => {
    if (!userUID || !(isCompanyOwner || userPermissions.canManageCompanySettings)) {
      return;
    }

    setCompanySaving(true);
    try {
      await update(dbRef(database, `companies/${userUID}`), {
        companyName: companyData.companyName,
        city: companyData.city,
        country: companyData.country,
        description: companyData.description,
        logoUrl: companyData.logoUrl
      });
      setIsEditing(false);
      setOriginalCompanyData(null);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(uiText('Failed to save company details. Please try again.'));
    } finally {
      setCompanySaving(false);
    }
  };

  const handleEditToggle = () => {
    if (!canManageCompanySettings) {
      setIsEditing(false);
      setOriginalCompanyData(null);
      return;
    }

    if (!isEditing) {
      setOriginalCompanyData(companyData);
      setIsEditing(true);
      return;
    }

    if (originalCompanyData) {
      setCompanyData(originalCompanyData);
    }
    setIsEditing(false);
    setOriginalCompanyData(null);
  };

  const handleLogoClick = () => {
    if (!(isCompanyOwner || userPermissions.canManageCompanySettings)) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userUID || !(isCompanyOwner || userPermissions.canManageCompanySettings)) return;

    setLogoUploading(true);
    try {
      const companyLogoRef = storageRef(storage, `companyLogos/${userUID}`);
      await uploadBytes(companyLogoRef, file);
      const url = await getDownloadURL(companyLogoRef);
      await update(dbRef(database, `companies/${userUID}`), { logoUrl: url });
      setCompanyData((previous) => ({ ...previous, logoUrl: url }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert(uiText('Failed to upload the company logo. Please try again.'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handlePersonalPhotoClick = () => {
    if (personalPhotoUploading || personalPhotoRemoving) return;
    if (personalFileInputRef.current) {
      personalFileInputRef.current.click();
    }
  };

  const handlePersonalPhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser || personalPhotoUploading || personalPhotoRemoving) return;

    setPersonalPhotoError('');
    setPersonalPhotoUploading(true);
    try {
      const profilePhotoRef = storageRef(storage, `userProfiles/${currentUser.uid}`);
      await uploadBytes(profilePhotoRef, file);
      const url = await getDownloadURL(profilePhotoRef);

      try {
        await updateProfile(currentUser, { photoURL: url });
        setPersonalPhotoUrl(url);
      } catch (error) {
        console.error('Error updating auth profile photo:', error);
      }

      if (userUID && isCompanyOwner) {
        try {
          await update(dbRef(database, `companies/${userUID}`), { ownerPhotoUrl: url });
        } catch (error) {
          console.error('Error updating ownerPhotoUrl in RTDB:', error);
        }
      }
    } catch (error) {
      console.error('Error uploading personal photo:', error);
      alert(uiText('Failed to upload photo. Please try again.'));
    } finally {
      setPersonalPhotoUploading(false);
      if (personalFileInputRef.current) personalFileInputRef.current.value = '';
    }
  };

  const handlePersonalPhotoRemove = async () => {
    if (!currentUser || personalPhotoUploading || personalPhotoRemoving) return;
    setPersonalPhotoRemoving(true);
    setPersonalPhotoError('');
    try {
      await removePersonalPhoto(currentUser, isCompanyOwner ? userUID : null);
      setPersonalPhotoUrl('');
      if (personalFileInputRef.current) personalFileInputRef.current.value = '';
    } catch (error) {
      console.error('Error removing profile photo:', error.code);
      setPersonalPhotoError(t('account.removePhotoError'));
    } finally {
      setPersonalPhotoRemoving(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const createCustomerPortal = httpsCallable(functions, 'createCustomerPortalUnified');
      const response = await createCustomerPortal({
        returnUrl: window.location.href
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error accessing customer portal:', error);

      if (error.code === 'not-found') {
        alert(uiText('No subscription found. Please subscribe first.'));
      } else if (error.code === 'unauthenticated') {
        alert(uiText('Please log in to access the billing portal.'));
      } else {
        alert(uiText('Failed to access billing portal. Please try again.'));
      }
    }
  };

  const getNextTierPlan = (currentPlanId, productType) => {
    if (productType === 'ats') {
      if (currentPlanId === 'ats_small') return { planId: 'ats_medium', name: 'Medium Teams', seats: 20 };
      if (currentPlanId === 'ats_medium') return { planId: 'ats_large', name: 'Large Teams', seats: 50 };
      return null;
    }

    if (productType === 'bundle') {
      if (currentPlanId === 'bundle_small') return { planId: 'bundle_medium', name: 'Medium Teams', seats: 20 };
      if (currentPlanId === 'bundle_medium') return { planId: 'bundle_large', name: 'Large Teams', seats: 50 };
      return null;
    }

    return null;
  };

  const inviteMember = async () => {
    if (!userUID) return;
    if (!(isCompanyOwner || userPermissions.canManageTeam)) {
      alert(uiText('You do not have permission to invite or manage team members.'));
      return;
    }

    const currentPlanId = subscription?.planId || subscriptionData?.planId;
    const currentProductType = subscription?.productType || subscriptionData?.productType;
    const subscriptionStatus = subscription?.status;

    if (!subscriptionStatus || !['active', 'trialing', 'unlimited'].includes(subscriptionStatus)) {
      if (accessType === 'trial') {
        alert(uiText('Team invites require a paid subscription. Please upgrade to continue inviting team members.'));
        setShowSubscriptionPopup(true);
        return;
      }

      alert(uiText('You need an active subscription to invite team members.'));
      setShowSubscriptionPopup(true);
      return;
    }

    if (currentProductType === 'tse') {
      alert(uiText('Team invites are only available with ATS or Bundle plans. Please upgrade to add team members.'));
      setShowSubscriptionPopup(true);
      return;
    }

    const seatLimit = getSeatLimitFromPlanId(currentPlanId, currentProductType);
    if (seatLimit === null) {
      alert(uiText('You need an ATS or Bundle subscription to invite team members.'));
      setShowSubscriptionPopup(true);
      return;
    }

    const totalSeats = teamMembers.length + pendingInvites.length;
    if (seatLimit !== 'unlimited' && totalSeats >= seatLimit) {
      const nextTier = getNextTierPlan(currentPlanId, currentProductType);
      if (nextTier) {
        alert(uiText("You've reached your plan's seat limit ({{seats}} seats). Upgrade to {{plan}} ({{nextSeats}} seats) to invite more team members.", { seats: seatLimit, plan: nextTier.name, nextSeats: nextTier.seats }));
      } else {
        alert(uiText("You've reached your plan's seat limit ({{seats}} seats). Please contact sales for a custom plan.", { seats: seatLimit }));
      }
      setShowSubscriptionPopup(true);
      return;
    }

    setShowInviteModal(true);
  };

  const handleInviteSubmit = async (memberForm) => {
    if (!canManageTeam) {
      alert(uiText('You do not have permission to invite or manage team members.'));
      setShowInviteModal(false);
      return;
    }

    const inviteData = buildInvitePayload(memberForm);
    if (!inviteData.email) return;

    setInviteLoading(true);

    if (!inviteData.email.includes('@') || !inviteData.email.includes('.')) {
      alert(uiText('Please enter a valid email address.'));
      setInviteLoading(false);
      return;
    }

    const normalizedEmail = inviteData.email.trim().toLowerCase();
    const emailAlreadyUsed = [...teamMembers, ...pendingInvites].some(
      (entry) => String(entry.email || '').trim().toLowerCase() === normalizedEmail
    );

    if (emailAlreadyUsed) {
      alert(uiText('This email already belongs to an active teammate or pending invite.'));
      setInviteLoading(false);
      return;
    }

    try {
      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/send-team-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employerUid: userUID,
          email: inviteData.email,
          companyName: companyData.companyName,
          initialPermissions: inviteData.permissions,
          collaboratorProfile: {
            firstName: inviteData.firstName,
            lastName: inviteData.lastName,
            name: inviteData.name,
            position: inviteData.position,
            departmentScope: inviteData.departmentScope,
            departments: inviteData.departments,
            communityChannels: inviteData.communityChannels
          }
        })
      });

      const result = await response.json();
      if (!result.success) {
        console.error('Error sending invite:', result.error);
        alert(uiText('Failed to send invitation. Please try again.'));
        return;
      }

      const invitesRef = dbRef(database, `companies/${userUID}/pendingInvites`);
      const newInviteRef = push(invitesRef);
      const teamProfileRef = dbRef(
        database,
        `companies/${userUID}/teamAccessProfiles/${newInviteRef.key}`
      );

      await set(newInviteRef, {
        email: inviteData.email,
        firstName: inviteData.firstName,
        lastName: inviteData.lastName,
        name: inviteData.name,
        position: inviteData.position,
        departmentScope: inviteData.departmentScope,
        departments: inviteData.departments,
        communityChannels: inviteData.communityChannels,
        invitedAt: serverTimestamp(),
        permissions: inviteData.permissions
      });

      await set(teamProfileRef, {
        uid: newInviteRef.key,
        email: inviteData.email,
        firstName: inviteData.firstName,
        lastName: inviteData.lastName,
        name: inviteData.name,
        position: inviteData.position,
        departmentScope: inviteData.departmentScope,
        departments: inviteData.departments,
        communityChannels: inviteData.communityChannels,
        permissions: inviteData.permissions,
        updatedAt: serverTimestamp()
      });

      setShowInviteModal(false);
      await loadTeamData(userUID);
      alert(uiText('Invitation sent to {{email}}!', { email: inviteData.email }));
    } catch (error) {
      console.error('Error inviting member:', error);
      alert(uiText('Failed to send invitation. Please try again.'));
    } finally {
      setInviteLoading(false);
    }
  };

  const removeTeamMember = async (memberOrId, email, sourceType = 'collaborator') => {
    if (!(isCompanyOwner || userPermissions.canManageTeam)) {
      alert(uiText('You do not have permission to manage team members.'));
      return;
    }

    const rawMember = typeof memberOrId === 'object' && memberOrId !== null
      ? memberOrId
      : {
          uid: memberOrId,
          email,
          sourceType,
          ...findTeamMemberByIdentity({ uid: memberOrId, email })
        };
    const targetMember = normalizeMemberRecord(rawMember);
    const memberId = targetMember.uid || targetMember.key;

    if (!canRemoveTeamMember(targetMember)) {
      alert(isCurrentUserMember(targetMember)
        ? 'Only the workspace owner can remove your account from the workspace.'
        : 'Only the workspace owner can remove workspace admins.');
      return;
    }

    if (!window.confirm('WARNING: Are you sure you want to delete this collaborator? This will delete their account.')) {
      return;
    }

    setTeamActionKey(`remove-member:${memberId}`);
    try {
      if (targetMember.sourceType === 'profile') {
        const removalKeys = getTeamProfileLookupKeys({ uid: memberId, email: targetMember.email });
        await Promise.all(removalKeys.map((profileKey) => remove(dbRef(database, `companies/${userUID}/teamAccessProfiles/${profileKey}`))));
        await loadTeamData(userUID);
        return;
      }

      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/remove-collaborator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: memberId, email: targetMember.email })
      });

      const result = await response.json();
      if (!result.success) {
        console.error('Error deleting user:', result.error);
        return;
      }

      const removalKeys = getTeamProfileLookupKeys({ uid: memberId, email: targetMember.email });
      await Promise.all(removalKeys.map((profileKey) => remove(dbRef(database, `companies/${userUID}/teamAccessProfiles/${profileKey}`))));
      await loadTeamData(userUID);
    } catch (error) {
      console.error('Error removing team member:', error);
      alert(uiText('Failed to remove the team member. Please try again.'));
    } finally {
      setTeamActionKey('');
    }
  };

  const removePendingInvite = async (invite) => {
    if (!(isCompanyOwner || userPermissions.canManageTeam)) {
      alert(uiText('You do not have permission to manage team members.'));
      return;
    }

    if (!window.confirm('Are you sure you want to delete this invite?')) {
      return;
    }

    setTeamActionKey(`remove-invite:${invite.key}`);
    try {
      await remove(dbRef(database, `companies/${userUID}/pendingInvites/${invite.key}`));
      const removalKeys = getTeamProfileLookupKeys({
        uid: invite.key || invite.uid,
        email: invite.email,
        fallbackId: invite.key
      });
      await Promise.all(removalKeys.map((profileKey) => remove(dbRef(database, `companies/${userUID}/teamAccessProfiles/${profileKey}`))));
      await loadTeamData(userUID);
    } catch (error) {
      console.error('Error removing invite:', error);
      alert(uiText('Failed to cancel the invite. Please try again.'));
    } finally {
      setTeamActionKey('');
    }
  };

  const handleTransferOwnership = () => {
    const subject = encodeURIComponent(`Ownership Transfer Request: ${companyData.companyName}`);
    window.location.href = `mailto:info@jobseeqr.co?subject=${subject}`;
  };

  const handleToggleJobApprovals = async (newValue) => {
    if (!(isCompanyOwner || userPermissions.canManageCompanySettings) || !userUID) {
      alert(uiText('You do not have permission to change approval settings.'));
      return;
    }

    setApprovalJobPostings(newValue);
    setApprovalBusy((previous) => ({ ...previous, jobPostings: true }));
    updatingApprovalRef.current = true;
    lastToggleTimeRef.current = Date.now();

    try {
      await set(dbRef(database, `companies/${userUID}/settings/approvals`), {
        jobPostings: newValue,
        offerLetters: approvalOfferLetters === true
      });
    } catch (error) {
      console.error('Error updating job posting approvals:', error);
      setApprovalJobPostings(!newValue);
      alert(uiText('Failed to update job approval settings. Please try again.'));
    } finally {
      setApprovalBusy((previous) => ({ ...previous, jobPostings: false }));
      setTimeout(() => {
        updatingApprovalRef.current = false;
      }, 1000);
    }
  };

  const handleToggleOfferApprovals = async (newValue) => {
    if (!(isCompanyOwner || userPermissions.canManageCompanySettings) || !userUID) {
      alert(uiText('You do not have permission to change approval settings.'));
      return;
    }

    setApprovalOfferLetters(newValue);
    setApprovalBusy((previous) => ({ ...previous, offerLetters: true }));
    updatingApprovalRef.current = true;
    lastToggleTimeRef.current = Date.now();

    try {
      await set(dbRef(database, `companies/${userUID}/settings/approvals`), {
        jobPostings: approvalJobPostings === true,
        offerLetters: newValue
      });
    } catch (error) {
      console.error('Error updating offer letter approvals:', error);
      setApprovalOfferLetters(!newValue);
      alert(uiText('Failed to update offer approval settings. Please try again.'));
    } finally {
      setApprovalBusy((previous) => ({ ...previous, offerLetters: false }));
      setTimeout(() => {
        updatingApprovalRef.current = false;
      }, 1000);
    }
  };

  const handleToggleCommunityFeature = async (newValue) => {
    if (!(isCompanyOwner || userPermissions.canManageCompanySettings) || !userUID) {
      alert(uiText('You do not have permission to change community settings.'));
      return;
    }

    const previousValue = companyData.customFeatures?.community !== false;
    setCompanyData((previous) => ({
      ...previous,
      customFeatures: {
        ...(previous.customFeatures || {}),
        community: newValue
      }
    }));
    writeCommunityFeatureSessionCache(currentUser?.uid, userUID, newValue);

    try {
      await set(dbRef(database, `companies/${userUID}/settings/customFeatures`), {
        community: newValue
      });
    } catch (error) {
      console.error('Error updating community feature setting:', error);
      setCompanyData((previous) => ({
        ...previous,
        customFeatures: {
          ...(previous.customFeatures || {}),
          community: previousValue
        }
      }));
      writeCommunityFeatureSessionCache(currentUser?.uid, userUID, previousValue);
      alert(uiText('Failed to update community feature setting. Please try again.'));
    }
  };

  const [applicationRoutingBusy, setApplicationRoutingBusy] = useState(false);
  const handleApplicationMethodChange = async (value) => {
    if (!(isCompanyOwner || userPermissions.canManageCompanySettings) || !userUID || applicationRoutingBusy) return;
    if (!['ats', 'external'].includes(value)) return;
    const previousValue = companyData.defaultApplicationMethod || 'ats';
    setApplicationRoutingBusy(true);
    setCompanyData(previous => ({ ...previous, defaultApplicationMethod: value }));
    try {
      await set(dbRef(database, `companies/${userUID}/settings/defaultApplicationMethod`), value);
    } catch (error) {
      setCompanyData(previous => ({ ...previous, defaultApplicationMethod: previousValue }));
      alert(uiText('Failed to update application routing. Please try again.'));
    } finally {
      setApplicationRoutingBusy(false);
    }
  };

  const handleToggleAgencyJobPosting = async (newValue) => {
    if (!(isCompanyOwner || userPermissions.canManageCompanySettings) || !userUID) return;
    const previousValue = companyData.agencyJobPostingEnabled === true;
    setCompanyData((previous) => ({ ...previous, agencyJobPostingEnabled: newValue }));
    try {
      await set(dbRef(database, `companies/${userUID}/settings/agencyJobPostingEnabled`), newValue);
    } catch (error) {
      console.error('Error updating agency job posting setting:', error);
      setCompanyData((previous) => ({ ...previous, agencyJobPostingEnabled: previousValue }));
      alert(uiText('Failed to update agency job posting setting. Please try again.'));
    }
  };

  const openPermissionsEditor = async (member) => {
    if (!(isCompanyOwner || userPermissions.canManageTeam)) {
      alert(uiText('You do not have permission to manage team access.'));
      return;
    }

    if (!canEditTeamMember(member)) {
      alert(isCurrentUserMember(member)
        ? 'Only the workspace owner can change your workspace access.'
        : 'Only the workspace owner can change workspace admin access.');
      return;
    }

    setMemberEditorLoading(true);

    try {
      const collabRef = dbRef(database, `collaborators/${member.uid}`);
      const snapshot = await get(collabRef);
      const latestMember = snapshot.exists()
        ? normalizeMemberRecord({
            uid: member.uid,
            sourceType: member.sourceType || 'collaborator',
            ...snapshot.val(),
            ...member
          })
        : normalizeMemberRecord(member);

      setEditingMember(latestMember);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setEditingMember(normalizeMemberRecord(member));
    }

    setMemberEditorLoading(false);
  };

  const savePermissions = async (memberForm) => {
    if (!editingMember || !(isCompanyOwner || userPermissions.canManageTeam)) return;

    if (!canEditTeamMember(editingMember)) {
      alert(isCurrentUserMember(editingMember)
        ? 'Only the workspace owner can change your workspace access.'
        : 'Only the workspace owner can change workspace admin access.');
      return;
    }

    const collaboratorPayload = buildInvitePayload({
      ...editingMember,
      ...memberForm
    });

    if (!isCompanyOwner && hasWorkspaceAdminPermissions(collaboratorPayload.permissions)) {
      alert(uiText('Only the workspace owner can grant workspace admin access.'));
      return;
    }

    setMemberEditorLoading(true);
    try {
      if (editingMember.sourceType === 'collaborator') {
        await update(dbRef(database, `collaborators/${editingMember.uid}`), {
          firstName: collaboratorPayload.firstName,
          lastName: collaboratorPayload.lastName,
          name: collaboratorPayload.name,
          position: collaboratorPayload.position,
          departmentScope: collaboratorPayload.departmentScope,
          departments: collaboratorPayload.departments,
          communityChannels: collaboratorPayload.communityChannels,
          permissions: collaboratorPayload.permissions
        });
      }

      const teamProfileKey = getTeamProfileStorageKey(
        '',
        editingMember.sourceType === 'invite'
          ? (editingMember.key || editingMember.uid)
          : (editingMember.uid || editingMember.key)
      );

      await set(dbRef(database, `companies/${userUID}/teamAccessProfiles/${teamProfileKey}`), {
        uid: editingMember.sourceType === 'invite'
          ? (editingMember.key || editingMember.uid || '')
          : (editingMember.uid || editingMember.key || ''),
        email: collaboratorPayload.email,
        firstName: collaboratorPayload.firstName,
        lastName: collaboratorPayload.lastName,
        name: collaboratorPayload.name,
        position: collaboratorPayload.position,
        departmentScope: collaboratorPayload.departmentScope,
        departments: collaboratorPayload.departments,
        communityChannels: collaboratorPayload.communityChannels,
        permissions: collaboratorPayload.permissions,
        updatedAt: serverTimestamp()
      });

      await loadTeamData(userUID);
      setEditingMember(null);
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert(uiText('Failed to save permissions. Please try again.'));
    } finally {
      setMemberEditorLoading(false);
    }
  };

  const topPermissionLabels = (member) =>
    Object.entries(member.permissions || {})
      .filter(([, enabled]) => enabled)
      .map(([key]) => permissionLookup[key])
      .filter(Boolean)
      .slice(0, 3);

  const userPermissions = normalizePermissionState(
    isCompanyOwner ? OWNER_PERMISSIONS : currentAccessProfile.permissions
  );
  const canManageTeam = isCompanyOwner || userPermissions.canManageTeam;
  const canManageCompanySettings = isCompanyOwner || userPermissions.canManageCompanySettings;
  const canViewControls = canManageCompanySettings;

  useEffect(() => {
    if (!canManageCompanySettings && isEditing) {
      setIsEditing(false);
      setOriginalCompanyData(null);
    }
  }, [canManageCompanySettings, isEditing]);

  useEffect(() => {
    if (!canManageTeam) {
      setShowInviteModal(false);
      setEditingMember(null);
    }
  }, [canManageTeam]);

  const companyCoverageLabel = companyData.city && companyData.country
    ? `${companyData.city}, ${companyData.country}`
    : companyData.city || companyData.country || 'Location not set';

  return {
    accessType,
    approvalJobPostings,
    approvalOfferLetters,
    accountEditing,
    accountForm,
    accountSaving,
    approvalSettingsLoading,
    companyCoverageLabel,
    companyData,
    communityChannelOptions,
    companySaving,
    currentAccessProfile,
    currentUser,
    canEditTeamMember,
    canManageCompanySettings,
    canManageTeam,
    canRemoveTeamMember,
    canViewControls,
    departmentOptions,
    editingMember,
    fileInputRef,
    filteredPendingInvites,
    filteredTeamMembers,
    handleAccountEditToggle,
    handleAccountInputChange,
    handleAccountSave,
    handleEditToggle,
    handleInputChange,
    handleInviteSubmit,
    handleLogoChange,
    handleLogoClick,
    handleManageSubscription,
    handlePersonalPhotoChange,
    handlePersonalPhotoClick,
    handlePersonalPhotoRemove,
    handleSave,
    handleToggleJobApprovals,
    handleToggleOfferApprovals,
    handleToggleCommunityFeature,
    handleToggleAgencyJobPosting,
    handleApplicationMethodChange,
    applicationRoutingBusy,
    handleTransferOwnership,
    inviteLoading,
    inviteMember,
    isCompanyOwner,
    isEditing,
    isRTL,
    language,
    logoUploading,
    memberEditorLoading,
    openPermissionsEditor,
    pendingInvites,
    personalPhotoUploading,
    personalPhotoRemoving,
    personalPhotoError,
    personalFileInputRef,
    personalPhotoUrl,
    profileStats,
    profileLoading,
    approvalBusy,
    removePendingInvite,
    removeTeamMember,
    savePermissions,
    searchTerm,
    seatUsageCount,
    setEditingMember,
    setSearchTerm,
    setShowInviteModal,
    setShowSubscriptionPopup,
    setSidebarCollapsed,
    setTeamFilter,
    showInviteModal,
    showSubscriptionPopup,
    sidebarCollapsed,
    subscription,
    subscriptionData,
    t,
    teamFilter,
    teamMembers,
    teamActionKey,
    topPermissionLabels,
    trial,
    userPermissions,
    userUID,
    getEnabledPermissionCount,
    getTeamMemberActionStatus
  };
}
